/**
 * GlobalAnalysisDialog Component
 * Renders the analysis dialog globally, driven by the analysisDialogStore.
 * Mount once in DashboardLayout so any component can trigger it.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AnalysisDialog, type AnalysisFormData } from '@/components/analysis/AnalysisDialog';
import { InsufficientCreditsDialog } from '@/components/credits/InsufficientCreditsDialog';
import { BuyCreditsDialog } from '@/components/credits/BuyCreditsDialog';
import { requestsService } from '@/services/api/requests';
import { useAnalysisDialogStore } from '@/stores/analysisDialogStore';
import { useAuthStore } from '@/stores/authStore';

export function GlobalAnalysisDialog() {
  const navigate = useNavigate();
  const { isOpen, close } = useAnalysisDialogStore();
  const totalCredits = useAuthStore((state) => state.user?.credits ?? 0);
  const [showDialog, setShowDialog] = useState(false);
  const [isInsufficientCreditsOpen, setIsInsufficientCreditsOpen] = useState(false);
  const [isBuyCreditsOpen, setIsBuyCreditsOpen] = useState(false);
  const prevIsOpen = useRef(false);

  // When store.isOpen transitions to true, decide what to show
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      if (totalCredits <= 0) {
        setIsInsufficientCreditsOpen(true);
        close(); // reset store state
      } else {
        setShowDialog(true);
      }
    }
    if (!isOpen) {
      setShowDialog(false);
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, totalCredits, close]);

  const createRequestMutation = useMutation({
    mutationFn: (formData: AnalysisFormData) => requestsService.createRequest(formData),
    onSuccess: (response) => {
      close();
      navigate(`/analysis/${response.correlationId}`);
    },
  });

  const handleSubmit = (formData: AnalysisFormData) => {
    createRequestMutation.mutate(formData);
  };

  const handleBuyCredits = () => {
    setIsInsufficientCreditsOpen(false);
    setIsBuyCreditsOpen(true);
  };

  return (
    <>
      <AnalysisDialog
        isOpen={showDialog}
        onClose={close}
        onSubmit={handleSubmit}
        isSubmitting={createRequestMutation.isPending}
      />

      <InsufficientCreditsDialog
        isOpen={isInsufficientCreditsOpen}
        onClose={() => setIsInsufficientCreditsOpen(false)}
        onBuyCredits={handleBuyCredits}
        currentCredits={totalCredits}
      />

      <BuyCreditsDialog
        isOpen={isBuyCreditsOpen}
        onClose={() => setIsBuyCreditsOpen(false)}
      />
    </>
  );
}
